// Interpolation of an interference map over the sky hemisphere.
//
// Input points are {az, el, value} in degrees, value in dB (10·log10 of the SFU flux). Values are
// interpolated in dB, not in linear power, so that a single strong source does not
// smear across the whole neighbourhood.
//
// Two traps of an az/el grid:
//  - azimuth wraps around: 355° is next to 0°,
//  - the zenith (el = 90°) is one physical point for every azimuth.

export type SkyPoint = {
  az: number
  el: number
  value: number
  time?: Date
}

// eslint-disable-next-line no-unused-vars
export type Interpolator = (az: number, el: number) => number | null

export type SkyGrid = {
  grid: Array<Array<number | null>>
  step: number
  nAz: number
  nEl: number
}

export const DEG = Math.PI / 180
const FULL_CIRCLE = 360
const ZENITH_EL = 90
const EXACT_MATCH_DEGREES = 0.01
const MIN_NEIGHBOURS_FOR_GAP_FILL = 2
const DEFAULT_IDW_POWER = 2
const DEFAULT_IDW_RADIUS = 10

export function normAz(az: number) {
  return ((az % FULL_CIRCLE) + FULL_CIRCLE) % FULL_CIRCLE
}

// x = east, y = north, z = zenith
export function toUnit(az: number, el: number): [number, number, number] {
  const a = az * DEG
  const e = el * DEG
  const c = Math.cos(e)
  return [c * Math.sin(a), c * Math.cos(a), Math.sin(e)]
}

const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length

// A single missing node becomes the mean of its neighbours (at least 2 of 4, azimuth
// wrapped). Larger areas without data stay empty.
function fillSingleGaps(grid: SkyGrid['grid'], nAz: number, nEl: number): SkyGrid['grid'] {
  return grid.map((row, i) =>
    row.map((v, j) => {
      if (v !== null) return v
      const around = [
        grid[i][(j + 1) % nAz],
        grid[i][(j - 1 + nAz) % nAz],
        i > 0 ? grid[i - 1][j] : null,
        i < nEl - 1 ? grid[i + 1][j] : null,
      ].filter((n): n is number => n !== null)
      return around.length >= MIN_NEIGHBOURS_FOR_GAP_FILL ? mean(around) : null
    }),
  )
}

// Measurement points -> regular grid[elIdx][azIdx] (null = no measurement).
export function buildGrid(points: SkyPoint[], step: number): SkyGrid {
  const nAz = Math.round(FULL_CIRCLE / step)
  const nEl = Math.round(ZENITH_EL / step) + 1
  const sum = Array.from({ length: nEl }, () => new Float64Array(nAz))
  const cnt = Array.from({ length: nEl }, () => new Uint16Array(nAz))

  for (const p of points) {
    if (!Number.isFinite(p.value)) continue
    const i = Math.round(p.el / step)
    const j = Math.round(normAz(p.az) / step) % nAz
    if (i < 0 || i >= nEl) continue
    sum[i][j] += p.value
    cnt[i][j] += 1
  }

  const grid = sum.map((row, i) => Array.from(row, (s, j) => (cnt[i][j] ? s / cnt[i][j] : null)))

  // Zenith: one value for the whole row, however many azimuths it was measured at.
  const top = grid[nEl - 1].filter((v): v is number => v !== null)
  if (top.length) {
    grid[nEl - 1].fill(mean(top))
  }

  return { grid: fillSingleGaps(grid, nAz, nEl), step, nAz, nEl }
}

// Bilinear in (az, el) with azimuth wrap. Missing corners are skipped and the
// remaining weights renormalised.
export function bilinear(g: SkyGrid): Interpolator {
  const { grid, step, nAz, nEl } = g

  return (az, el) => {
    if (el < 0 || el > ZENITH_EL) return null
    const fa = normAz(az) / step
    const fe = el / step
    const j0 = Math.floor(fa) % nAz
    const j1 = (j0 + 1) % nAz
    const i0 = Math.min(Math.floor(fe), nEl - 1)
    const i1 = Math.min(i0 + 1, nEl - 1)
    const ta = fa - Math.floor(fa)
    const te = fe - i0

    const corners: Array<[number | null, number]> = [
      [grid[i0][j0], (1 - ta) * (1 - te)],
      [grid[i0][j1], ta * (1 - te)],
      [grid[i1][j0], (1 - ta) * te],
      [grid[i1][j1], ta * te],
    ]

    let ws = 0
    let vs = 0
    for (const [v, w] of corners) {
      if (v === null || w <= 0) continue
      ws += w
      vs += v * w
    }
    return ws > 0 ? vs / ws : null
  }
}

// Points bucketed into CELL_DEGREES × CELL_DEGREES az/el cells, so that an interpolation looks only
// at the cells a search radius can reach instead of at every point. On a 30k-point scan this is
// the difference between seconds and a fraction of a second per chart.
type PointCells = {
  // Points of cell c are order[start[c]] .. order[start[c + 1] - 1].
  start: Int32Array
  order: Int32Array
  // eslint-disable-next-line no-unused-vars
  near: (az: number, el: number, radius: number) => number[]
}

const CELL_DEGREES = 2
const AZ_CELLS = FULL_CIRCLE / CELL_DEGREES
const EL_CELLS = (2 * ZENITH_EL) / CELL_DEGREES

const azCell = (az: number) => Math.floor(normAz(az) / CELL_DEGREES) % AZ_CELLS
const elCell = (el: number) => Math.min(EL_CELLS - 1, Math.max(0, Math.floor((el + ZENITH_EL) / CELL_DEGREES)))

function indexCells(points: SkyPoint[]): PointCells {
  const cellOf = points.map((p) => elCell(p.el) * AZ_CELLS + azCell(p.az))
  const start = new Int32Array(EL_CELLS * AZ_CELLS + 1)
  cellOf.forEach((c) => {
    start[c + 1] += 1
  })
  for (let c = 0; c < EL_CELLS * AZ_CELLS; c++) start[c + 1] += start[c]
  const next = start.slice(0, -1)
  const order = new Int32Array(points.length)
  cellOf.forEach((c, k) => {
    order[next[c]] = k
    next[c] += 1
  })

  const near = (az: number, el: number, radius: number) => {
    const cells: number[] = []
    // A spherical cap of the radius spans asin(sin r / cos el) of azimuth, or all of it when it
    // reaches the zenith. One cell of margin on every side covers points on cell edges.
    const reachesPole = Math.abs(el) + radius >= ZENITH_EL
    const azSpan = reachesPole ? FULL_CIRCLE : Math.asin(Math.sin(radius * DEG) / Math.cos(el * DEG)) / DEG
    const azCount = Math.min(AZ_CELLS, 2 * Math.ceil(azSpan / CELL_DEGREES) + 3)
    const azFirst = azCount === AZ_CELLS ? 0 : azCell(az) - (azCount - 1) / 2
    for (let row = elCell(el - radius) - 1; row <= elCell(el + radius) + 1; row++) {
      if (row < 0 || row >= EL_CELLS) continue
      for (let i = 0; i < azCount; i++) {
        cells.push(row * AZ_CELLS + ((azFirst + i + AZ_CELLS) % AZ_CELLS))
      }
    }
    return cells
  }

  return { start, order, near }
}

// Inverse distance weighting over great-circle distance. No seam at 0°/360° and no
// singularity at the zenith; gaps are filled from the neighbourhood. Outside the
// radius it returns null instead of inventing values where nothing was measured.
export function idw(points: SkyPoint[], opts: { power?: number; radius?: number } = {}): Interpolator {
  const power = opts.power ?? DEFAULT_IDW_POWER
  const radius = opts.radius ?? DEFAULT_IDW_RADIUS
  const valid = points.filter((p) => Number.isFinite(p.value))
  const n = valid.length
  const ux = new Float64Array(n)
  const uy = new Float64Array(n)
  const uz = new Float64Array(n)
  const val = new Float64Array(n)

  valid.forEach((p, k) => {
    const u = toUnit(p.az, p.el)
    ux[k] = u[0]
    uy[k] = u[1]
    uz[k] = u[2]
    val[k] = p.value
  })

  const cosR = Math.cos(radius * DEG)
  const cosEps = Math.cos(EXACT_MATCH_DEGREES * DEG)
  const cells = indexCells(valid)

  return (az, el) => {
    const u = toUnit(az, el)
    let ws = 0
    let vs = 0
    // Points coinciding with the target (e.g. several zenith measurements) are averaged.
    let exactN = 0
    let exactSum = 0
    for (const cell of cells.near(az, el, radius)) {
      for (let i = cells.start[cell]; i < cells.start[cell + 1]; i++) {
        const k = cells.order[i]
        const d = u[0] * ux[k] + u[1] * uy[k] + u[2] * uz[k]
        if (d < cosR) continue
        if (d >= cosEps) {
          exactN += 1
          exactSum += val[k]
          continue
        }
        const angle = Math.acos(d)
        // Math.pow is several times slower than a product, and power 2 is the default.
        const w = power === DEFAULT_IDW_POWER ? 1 / (angle * angle) : Math.pow(angle, -power)
        ws += w
        vs += w * val[k]
      }
    }
    if (exactN) return exactSum / exactN
    return ws > 0 ? vs / ws : null
  }
}

// Restricts an interpolator to an elevation band (inclusive), so that a scan of a narrow
// band is not extrapolated across the sky by a generous IDW radius.
export function clipElevation(f: Interpolator, lo: number, hi: number): Interpolator {
  return (az, el) => (el < lo || el > hi ? null : f(az, el))
}
